import { useEffect, useRef } from "react";
import { Box } from "@mui/material";

// The site's animated brand gradient, drawn so the browser never repaints it.
//
// It used to be `background-position` animated on <body>. That property cannot
// be handed to the compositor, so the whole viewport repainted every frame,
// forever - and every glass blur on top of it re-ran with it. During a fast
// scroll the browser could not keep up, which is what "the site looks broken
// when I scroll" was. See CLAUDE.md, "Motion".
//
// Here the gradient is painted ONCE into a tiny canvas that is stretched to 4x
// the width of its container and slid sideways with a `transform` animation.
// Transform animations run on the compositor thread, so scrolling and the sweep
// cost no paint at all. A canvas rather than a 400%-wide gradient <div>: a div
// is rasterised at its CSS size, re-rastering tiles as it slides, while a canvas
// is uploaded once at its bitmap size (a quarter of the layer's resolution, see
// TEXEL) and stretched by the GPU. A linear gradient survives that stretch.
//
// The keyframes reproduce the old ones: background-position 0% -> 100% -> 0% at
// background-size 400% is a slide of -75% of the tile's width, 7s ease infinite.

const STOPS = ["#ee7752", "#e73c7e", "#23a6d5", "#23d5ab"];

// The old gradient was sized against the whole document (1000% of its height),
// so its band of colour differed on long and short pages. A fixed layer has one
// look everywhere; this default tile height, in container heights, sits between
// the homepage (long, calm) and the contact page (short, varied). A section that
// used to size its own gradient at 1000% passes 10.
const DEFAULT_VIRTUAL_HEIGHT = 30;

// One canvas texel per this many CSS pixels of the stretched layer. Chrome
// dithers canvas gradients, and whatever size the bitmap is, that dither grain
// is magnified by the stretch: at a fixed 512px bitmap it became visible
// "brickwork" blocks ~11px wide across a laptop screen. At 4px it is invisible,
// and the texture is still small - ~1.3 MB for a 1440x900 window, ~8 MB at 4K.
const TEXEL = 4;
const MAX_BITMAP = 4096; // stay well inside every GPU's texture size limit

// Paint a CSS `linear-gradient(-45deg, ...)` for a (4W x K*H) tile, as seen
// through its middle H-tall band, into a canvas that is later stretched
// non-uniformly to 4W x H. Isolines of a linear gradient stay parallel under a
// non-uniform scale but stop being perpendicular to the gradient vector, so the
// canvas gradient vector is solved for rather than mapped: t = a*cx + b*cy + c.
const paint = (canvas, width, height, virtualHeight) => {
  const tileW = 4 * width;
  const bitmapW = Math.max(1, Math.min(MAX_BITMAP, Math.round(tileW / TEXEL)));
  const bitmapH = Math.max(1, Math.min(MAX_BITMAP, Math.round(height / TEXEL)));
  if (canvas.width !== bitmapW) canvas.width = bitmapW;
  if (canvas.height !== bitmapH) canvas.height = bitmapH;
  const ctx = canvas.getContext("2d");
  if (!ctx) return;
  const tileH = virtualHeight * height;
  const d = Math.SQRT1_2; // -45deg points to the top-left: direction (-d, -d)
  const length = (tileW + tileH) * d;
  const startX = tileW / 2 + (d * length) / 2;
  const startY = tileH / 2 + (d * length) / 2;
  const bandTop = ((virtualHeight - 1) / 2) * height;
  const sx = tileW / bitmapW;
  const sy = height / bitmapH;

  const a = (sx * -d) / length;
  const b = (sy * -d) / length;
  const c = ((0 - startX) * -d + (bandTop - startY) * -d) / length;
  const norm = a * a + b * b;
  const x0 = (-c * a) / norm;
  const y0 = (-c * b) / norm;
  const x1 = x0 + a / norm;
  const y1 = y0 + b / norm;

  const gradient = ctx.createLinearGradient(x0, y0, x1, y1);
  STOPS.forEach((color, i) => gradient.addColorStop(i / (STOPS.length - 1), color));
  ctx.fillStyle = gradient;
  ctx.fillRect(0, 0, bitmapW, bitmapH);
};

const GradientSweep = ({ virtualHeight = DEFAULT_VIRTUAL_HEIGHT, sx }) => {
  const wrapRef = useRef(null);
  const canvasRef = useRef(null);

  useEffect(() => {
    const wrap = wrapRef.current;
    const canvas = canvasRef.current;
    if (!wrap || !canvas) return undefined;

    let timer;
    const draw = (width, height) => {
      if (width > 0 && height > 0) paint(canvas, width, height, virtualHeight);
    };

    if (typeof ResizeObserver === "undefined") {
      const { width, height } = wrap.getBoundingClientRect();
      draw(width, height);
      return undefined;
    }

    // The size comes from the observer, never from reading layout here. Its
    // first callback arrives right after the browser's own layout of the frame
    // and before that frame paints, so the canvas is ready without this effect
    // forcing a layout of the whole page mid-render (on a slowed phone that read
    // was ~180ms of the Work page's load). Later resizes are debounced: a
    // drag-resize would otherwise repaint every frame.
    let first = true;
    const observer = new ResizeObserver(([entry]) => {
      const { width, height } = entry.contentRect;
      clearTimeout(timer);
      if (first) {
        first = false;
        draw(width, height);
      } else {
        timer = setTimeout(() => draw(width, height), 150);
      }
    });
    observer.observe(wrap);
    return () => {
      clearTimeout(timer);
      observer.disconnect();
    };
  }, [virtualHeight]);

  return (
    <Box
      ref={wrapRef}
      aria-hidden="true"
      className="gradient-sweep"
      sx={{
        position: "absolute",
        inset: 0,
        overflow: "hidden",
        pointerEvents: "none",
        // Same midpoint colour, painted before the canvas draws on first frame.
        backgroundColor: "#8a6fa8",
        ...sx,
      }}
    >
      <Box
        component="canvas"
        ref={canvasRef}
        sx={{
          display: "block",
          width: "400%",
          height: "100%",
          "@keyframes gradientSweep": {
            "0%, 100%": { transform: "translateX(0)" },
            "50%": { transform: "translateX(-75%)" },
          },
          animation: "gradientSweep 7s ease infinite",
          "@media (prefers-reduced-motion: reduce)": { animation: "none" },
        }}
      />
    </Box>
  );
};

export default GradientSweep;
